import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { BookOpen, Lock, Sparkles, Crown, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { LiveYogaTrialBanner } from '../../components/LiveYogaTrialBanner';

/**
 * V13.14 — Learn Tab for learners.
 * Shows all courses the learner can discover (audience-filtered), with a
 * CTA tile per course based on ownership + tier availability.
 */
export default function LearnTab() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [ownedIds, setOwnedIds] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/access/learn-tab');
        setCourses(res.data.courses || []);
        setOwnedIds(res.data.owned_course_ids || []);
        setAudiences(res.data.audiences || []);
      } catch {
        toast.error('Failed to load courses');
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading…</div>;

  const owned = courses.filter((c) => c.access_state?.owned);
  const discover = courses.filter((c) => !c.access_state?.owned);

  return (
    <div className="space-y-8 p-4 sm:p-6 max-w-5xl mx-auto" data-testid="learn-tab">
      <LiveYogaTrialBanner />
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold">Learn</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Curated for {audiences.join(' + ')} — start where you are, go deeper when you're ready.
        </p>
      </div>

      {owned.length > 0 && (
        <section>
          <h2 className="text-base font-medium mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            Your courses
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {owned.map((c) => (
              <CourseCard key={c.id} course={c} navigate={navigate} owned />
            ))}
          </div>
        </section>
      )}

      {discover.length > 0 && (
        <section>
          <h2 className="text-base font-medium mb-3 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-rose-500" />
            Discover
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {discover.map((c) => (
              <CourseCard key={c.id} course={c} navigate={navigate} />
            ))}
          </div>
        </section>
      )}

      {courses.length === 0 && (
        <Card className="p-8 text-center">
          <BookOpen className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            No courses yet for your current stage. Check back soon!
          </p>
        </Card>
      )}
    </div>
  );
}

function CourseCard({ course, navigate, owned = false }) {
  const state = course.access_state || {};
  const tiersEnabled = course.tiers_enabled || [];
  const badge = course.marketing_badge;
  // V14.1 — owned-but-locked = sales gave the course but unlock_rule says wait
  // (e.g. "unlocks at week 32"). Show greyed card with the lock_message.
  const isOwnedButLocked = owned && !state.allowed && state.lock_message;

  return (
    <Card
      className={`overflow-hidden hover:shadow-lg transition-shadow ${isOwnedButLocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
      onClick={() => { if (!isOwnedButLocked) navigate(`/learner/learn/course/${course.id}`); }}
      data-testid={`learn-course-card-${course.id}`}
    >
      <div className={`aspect-video relative ${isOwnedButLocked ? 'bg-slate-100' : 'bg-gradient-to-br from-rose-100 to-purple-100'}`}>
        {course.preview_image_url ? (
          <img src={course.preview_image_url} alt={course.title} className={`w-full h-full object-cover ${isOwnedButLocked ? 'grayscale' : ''}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className={`h-10 w-10 ${isOwnedButLocked ? 'text-slate-400' : 'text-rose-400'}`} />
          </div>
        )}
        {badge && !isOwnedButLocked && (
          <Badge className="absolute top-2 right-2 bg-white/90 text-rose-700 border-rose-200 text-[10px]">
            {badge}
          </Badge>
        )}
        {!state.allowed && (
          <div className="absolute bottom-2 right-2 bg-white/90 rounded-full p-1.5">
            <Lock className="h-3 w-3 text-slate-600" />
          </div>
        )}
      </div>
      <CardContent className="p-4">
        <h3 className="font-medium text-base line-clamp-1">{course.title}</h3>
        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{course.description}</p>
        {isOwnedButLocked && (
          <div className="mt-2 p-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-900 flex items-start gap-1.5"
               data-testid={`learn-locked-msg-${course.id}`}>
            <Lock className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span>{state.lock_message}</span>
          </div>
        )}
        <div className="flex items-center justify-between mt-3">
          <span className="text-[11px] text-muted-foreground">
            {course.total_episodes} episodes
          </span>
          {isOwnedButLocked ? (
            <Badge variant="outline" className="text-[10px] h-5 bg-slate-50 text-slate-600 border-slate-200">
              <Lock className="h-2.5 w-2.5 mr-0.5" />Locked
            </Badge>
          ) : owned ? (
            <Button size="sm" variant="outline" className="h-7 text-[11px]">
              Continue →
            </Button>
          ) : state.allowed ? (
            <Button size="sm" className="h-7 text-[11px] bg-rose-500 hover:bg-rose-600">
              Start
            </Button>
          ) : (
            <div className="flex gap-1">
              {tiersEnabled.includes('free_trial') && (
                <Badge variant="outline" className="text-[10px] h-5">Free preview</Badge>
              )}
              {tiersEnabled.includes('paid_trial') && !tiersEnabled.includes('free_trial') && (
                <Badge variant="outline" className="text-[10px] h-5 border-rose-200 text-rose-700">
                  Paid trial
                </Badge>
              )}
              {tiersEnabled.includes('full') && (
                <Badge className="text-[10px] h-5 bg-rose-500">
                  <Crown className="h-2.5 w-2.5 mr-0.5" />Full
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
